import type { DataColumn, DataColumnWithType } from "../types"

interface Props {
    allColumns: DataColumn[]
    dimensions: {
        rows: number
        columns: number
    }
    encoding: {
        encode: (value: string) => Uint32Array
    }
}

const idRegex = new RegExp("(\\b|_|-)(?<id>id|ID)(\\b|_|-)", "g")

/**
 * Processes columns to determine their types and additional metadata.
 *
 * This function analyzes each column to determine its type based on the content of its cells
 * and computes additional metadata such as the quantity of tokens for text columns.
 *
 * ### Rules for Determining Column Types:
 *
 * 1. **List**:
 *    - If the number of unique values (`uniqValues.size`) multiplied by 2 is less than or equal
 *      to the number of cells with values (`cellsWithValues`), **and**
 *    - The number of unique values is less than or equal to 200, **and**
 *    - There are at least 10 cells with values,
 *    - **Then**, the column is determined to be of type `"list"`, and `options` are set to the
 *      array of unique values.
 *
 * 2. **ID**:
 *    - If the number of unique values is equal to the total number of values (`allValues.length`), **and**
 *    - The total number of values is equal to the number of rows (`dimensions.rows`), **and**
 *    - The column's label matches the regular expression `/(\b|_|-)(id|ID)(\b|_|-)/g` (i.e., the
 *      label contains "id" or "ID" as a whole word, possibly surrounded by word boundaries, underscores, or hyphens),
 *    - **Then**, the column is determined to be of type `"id"`.
 *
 * 3. **Date**, **Number**, **Boolean**:
 *    - If all cell values with data are of the same type (`valueTypes.size === 1`):
 *        - If the values are instances of `Date`, **then** the type is `"date"`.
 *        - Otherwise, the type is the single value type present (`"number"` or `"boolean"`).
 *
 * 4. **Text**:
 *    - If none of the above conditions are met, the column is determined to be of type `"text"`.
 *    - For columns initially identified as `"string"`, the type is converted to `"text"`.
 *
 * ### Additional Metadata:
 *
 * - **Options**:
 *    - For columns of type `"list"`, `options` contain the array of unique stringified values.
 * - **Quantity**:
 *    - For columns of type `"text"`, `quantity` is calculated as the total number of tokens in all
 *      cell values using the provided `encoding` utility.
 *    - For other types, `quantity` is set to the number of cells with values (`cellsWithValues`).
 *
 * @param {Object} params - The parameters for the function.
 * @param {DataColumn[]} params.allColumns - The array of all columns to process.
 * @param {Dimensions} params.dimensions - The dimensions of the data.
 * @param {import("@dqbd/tiktoken").Tiktoken} params.encoding - The encoding utility for processing text.
 * @returns {Omit<DataColumnWithType, "cost">[]} An array of columns with determined types and additional metadata, excluding the "cost" property.
 */
export function getColumnsWithType({ allColumns, dimensions, encoding }: Props): Omit<DataColumnWithType, "cost">[] {
    return allColumns.map((column) => {
        const uniqValues = new Set<unknown>()
        const allValues: (string | number | boolean)[] = []
        const valueTypes = new Set<string>()
        let cellsWithValues = 0

        column.cells.forEach((cell) => {
            if (cell !== null && cell !== undefined) {
                cellsWithValues++

                if (typeof cell === "string") {
                    valueTypes.add("string")
                    if (cell.includes(";")) {
                        const values = cell.split(";")
                        values.forEach((value) => {
                            uniqValues.add(value)
                            allValues.push(value)
                        })
                    } else {
                        uniqValues.add(cell)
                        allValues.push(cell)
                    }
                } else {
                    uniqValues.add(cell)
                    valueTypes.add(typeof cell)
                    allValues.push(cell)
                }
            }
        })

        let rawType: "string" | "number" | "boolean" | "list" | "date" | "id"
        let options: string[] | undefined

        if (uniqValues.size * 2 <= cellsWithValues && uniqValues.size <= 200 && cellsWithValues >= 10) {
            rawType = "list"
            options = Array.from(uniqValues).map((v) => String(v))
        } else if (
            uniqValues.size === allValues.length &&
            allValues.length === dimensions.rows &&
            column.label.match(idRegex) !== null
        ) {
            rawType = "id"
        } else if (valueTypes.size === 1) {
            if (uniqValues.values().next().value instanceof Date) {
                rawType = "date"
            } else {
                rawType = valueTypes.values().next().value as "string" | "number" | "boolean"
            }
        } else {
            rawType = "string"
        }

        const type = rawType === "string" ? "text" : rawType

        const quantity =
            type === "text"
                ? allValues.reduce<number>((acc, value) => {
                      try {
                          const tokens = encoding.encode(String(value))
                          return acc + tokens.length
                      } catch (error) {
                          console.error("Failed to encode value", column, value)
                          window.newrelic.noticeError(new Error("Failed to encode value during project create form"), {
                              type,
                              value,
                          })
                          throw error
                      }
                  }, 0)
                : cellsWithValues

        return {
            ...column,
            type,
            options,
            quantity,
        }
    })
}
